import { shipmentService } from '../services/shipmentService.js'
import { parseBlobErrorMessage, printPdfBlob } from './printPdfBlob.js'
import Alert from '../components/common/Alert.js'

const QR_PRINT_ROLES = ['System Admin', 'Admin', 'Accountant', 'Hub Admin']

export function canPrintShipmentQr(user) {
  return QR_PRINT_ROLES.includes(user?.role)
}

async function bodyText(data) {
  if (typeof data === 'string') return data
  if (data instanceof Blob) return data.text()
  return ''
}

function messageFromBody(text) {
  const raw = String(text || '').trim()
  if (!raw || raw.startsWith('%PDF') || raw.includes('<html')) {
    return 'Failed to print QR code'
  }
  try {
    const parsed = JSON.parse(raw)
    return parsed.error || parsed.message || 'Failed to print QR code'
  } catch {
    return 'Failed to print QR code'
  }
}

async function qrPrintErrorMessage(error) {
  const data = error?.response?.data
  if (typeof data === 'string' || data instanceof Blob) {
    return messageFromBody(await bodyText(data))
  }
  return parseBlobErrorMessage(error, 'Failed to print QR code')
}

async function finishPdfPrint(blob, shipmentId, silent) {
  const result = await printPdfBlob(blob, `QR_Code_${shipmentId}.pdf`)
  if (result?.method === 'cancelled') return
  if (!silent && result?.method === 'blocked') {
    Alert.error('Could not open the print dialog')
  }
}

export async function printShipmentQrCode(shipmentId, { onClose, silent = false } = {}) {
  try {
    if (onClose) onClose()
    const response = await shipmentService.getQRCode(shipmentId)
    const payload = response?.data
    const blob = payload instanceof Blob
      ? payload
      : new Blob([payload], { type: 'application/pdf' })
    await finishPdfPrint(blob, shipmentId, silent)
  } catch (error) {
    if (!silent) {
      Alert.error(await qrPrintErrorMessage(error))
    }
  }
}

/** Extra QR print after booking when the location hub has Print QR on. */
export async function maybePrintQrWithBookingReceipt(user, shipmentId) {
  if (!user?.print_qr_with_booking_receipt || !shipmentId) return
  await printShipmentQrCode(shipmentId)
}

export async function downloadShipmentQrCodesBulk(shipmentIds, filename = 'shipment_qr_codes.pdf') {
  const ids = (shipmentIds || []).filter(Boolean)
  if (!ids.length) {
    Alert.error('No shipments found for QR download')
    return
  }
  try {
    const response = await shipmentService.bulkDownloadQRCodes(ids)
    const { downloadBlob } = await import('./downloadBlob.js')
    const saved = await downloadBlob(response.data, filename, 'application/pdf')
    if (saved?.method === 'cancelled') return
    Alert.success('Shipment QR codes downloaded')
  } catch (error) {
    Alert.error(await parseBlobErrorMessage(error, 'Failed to download shipment QR codes'))
  }
}
